import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useBatchUpdateCustomAgentResultsMutation,
  useLazyListCustomAgentResultItemsQuery,
  useListCustomAgentResultItemsQuery,
  usePatchCustomAgentResultFeedbackMutation,
  type CustomAgentResultApprovalStatus,
  type CustomAgentResultItem
} from '@axilon-apps/domains-ontology-ui-state'
import { FollowUpVersionPicker } from '../../pages/RunPage/FollowUpVersionPicker'
import { HistoricalVersionBanner } from '../../pages/RunPage/FollowUpBanners'
import {
  findResultItemAtRevision,
  useInsightRunRevision
} from '../../pages/RunPage/useInsightRunRevision'

import { Button } from '../ui/button'
import { Empty } from '../primitives/Empty'
import { StatusPill } from '../primitives/StatusPill'
import type { Status } from '../primitives/types'
import { runPageHref } from '../../lib/component-links'
import { formatRelativeTime } from '../../lib/time'
import { cn } from '../../lib/utils'
import { SchemaAwareResultView } from './SchemaAwareResultView'
import { formatScalar, isReviewableResultType, parseObjectSchema } from './schema-aware-result'

const DEFAULT_PAGE_SIZE = 25

interface CustomAgentResultsBrowserProps {
  configId: string
  /** Config `output_schema` — used to render each shredded item's fragment. */
  outputSchema: Record<string, unknown>
  /** Page size for the item list (default 25). Use a smaller value in stacked "All" views. */
  pageSize?: number
}

/**
 * Item-stream browser over shredded `custom_agent_results` with per-item
 * approve / reject / feedback. Selecting a row shows the item payload and
 * a link to the producing run.
 */
export function CustomAgentResultsBrowser({
  configId,
  outputSchema,
  pageSize = DEFAULT_PAGE_SIZE
}: CustomAgentResultsBrowserProps) {
  const [extraRows, setExtraRows] = useState<CustomAgentResultItem[]>([])
  const [nextToken, setNextToken] = useState<string | undefined>(undefined)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [statusFilter, setStatusFilter] = useState<CustomAgentResultApprovalStatus | ''>('')

  const { data, isLoading, isError } = useListCustomAgentResultItemsQuery({
    configId,
    pageSize,
    approvalStatus: statusFilter || undefined
  })
  const [fetchMore] = useLazyListCustomAgentResultItemsQuery()

  useEffect(() => {
    setExtraRows([])
    setNextToken(data?.next_page_token)
  }, [configId, statusFilter, data?.results, data?.next_page_token])

  const rows = useMemo(() => {
    const head = data?.results ?? []
    if (extraRows.length === 0) return head
    const seen = new Set(head.map(rowKey))
    return [...head, ...extraRows.filter((r) => !seen.has(rowKey(r)))]
  }, [data?.results, extraRows])

  useEffect(() => {
    if (rows.length === 0) {
      setSelectedKey(null)
      return
    }
    if (!selectedKey || !rows.some((r) => rowKey(r) === selectedKey)) {
      setSelectedKey(rowKey(rows[0]))
    }
  }, [rows, selectedKey])

  const selected = rows.find((r) => rowKey(r) === selectedKey) ?? null
  const revision = useInsightRunRevision(selected?.run_id ?? null, configId)
  const displayItem = useMemo(() => {
    if (!selected) return null
    if (revision.revisionItems.length > 0) {
      return (
        findResultItemAtRevision(selected, revision.revisionItems) ??
        (revision.viewingHistory ? null : selected)
      )
    }
    return revision.viewingHistory ? null : selected
  }, [selected, revision.viewingHistory, revision.revisionItems])
  const handleLoadMore = async () => {
    if (!nextToken) return
    setLoadingMore(true)
    try {
      const page = await fetchMore({
        configId,
        pageSize,
        pageToken: nextToken,
        approvalStatus: statusFilter || undefined
      }).unwrap()
      setExtraRows((prev) => {
        const seen = new Set(prev.map(rowKey))
        return [...prev, ...page.results.filter((r) => !seen.has(rowKey(r)))]
      })
      setNextToken(page.next_page_token)
    } finally {
      setLoadingMore(false)
    }
  }

  if (isLoading && rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-card px-4 py-6 text-center text-sm text-ink-3 shadow-sm">
        Loading results…
      </div>
    )
  }

  const versionPicker = revision.showPicker ? (
    <FollowUpVersionPicker
      currentRevision={revision.currentRevision}
      selectedRevision={revision.selectedRevision}
      revisions={revision.revisions}
      onSelect={(rev) => revision.setViewRevision(rev === revision.currentRevision ? null : rev)}
    />
  ) : null

  const selectedDetail = selected ? (
    <>
      {revision.viewingHistory ? (
        <HistoricalVersionBanner
          revision={revision.selectedRevision}
          onBackToCurrent={() => revision.setViewRevision(null)}
        />
      ) : null}
      {revision.viewingHistory && revision.isLoadingRevision ? (
        <p className="m-0 text-sm text-ink-3">Loading this version…</p>
      ) : revision.viewingHistory && revision.isRevisionError ? (
        <p className="m-0 text-sm text-down">Couldn’t load this version.</p>
      ) : revision.viewingHistory && !displayItem ? (
        <p className="m-0 text-sm text-ink-3">This finding was not present in this version.</p>
      ) : displayItem ? (
        <ItemDetailPanel
          item={displayItem}
          outputSchema={outputSchema}
          expectedRevision={displayItem.revision ?? revision.currentRevision}
          readOnly={revision.viewingHistory || displayItem.superseded}
        />
      ) : null}
    </>
  ) : null

  if (isError && rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-card px-4 py-6 text-center text-sm text-down shadow-sm">
        Failed to load results.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-11 text-ink-3" htmlFor={`approval-filter-${configId}`}>
          Review status
        </label>
        <select
          id={`approval-filter-${configId}`}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CustomAgentResultApprovalStatus | '')}
          className="h-7 rounded-md border border-line bg-card px-2 font-mono text-12 text-ink-2 outline-none focus:border-brand"
        >
          <option value="">All</option>
          <option value="awaiting_review">Awaiting review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        {versionPicker ? <div className="ml-auto">{versionPicker}</div> : null}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-line bg-card shadow-sm">
          <Empty
            title="No result items yet"
            hint="Completed runs shred schema fields into reviewable items here."
          />
        </div>
      ) : (
        <>
          <ItemsTable
            rows={rows}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            outputSchema={outputSchema}
          />

          {nextToken ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={() => void handleLoadMore()}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}

          {selectedDetail}
        </>
      )}
    </div>
  )
}

function ItemsTable({
  rows,
  selectedKey,
  onSelect,
  outputSchema
}: {
  rows: CustomAgentResultItem[]
  selectedKey: string | null
  onSelect: (key: string) => void
  outputSchema: Record<string, unknown>
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full caption-bottom text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="h-9 px-3 text-left align-middle text-xs font-medium text-ink-3">
                When
              </th>
              <th className="h-9 px-3 text-left align-middle text-xs font-medium text-ink-3">
                Type
              </th>
              <th className="h-9 px-3 text-left align-middle text-xs font-medium text-ink-3">
                Preview
              </th>
              <th className="h-9 px-3 text-left align-middle text-xs font-medium text-ink-3">
                Status
              </th>
              <th className="h-9 px-3 text-right align-middle text-xs font-medium text-ink-3">
                Run
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row)
              const selected = key === selectedKey
              return (
                <tr
                  key={key}
                  data-state={selected ? 'selected' : undefined}
                  className={cn(
                    'cursor-pointer border-b border-line last:border-0 transition-colors hover:bg-muted/40',
                    selected && 'bg-muted/50'
                  )}
                  onClick={() => onSelect(key)}
                >
                  <td className="px-3 py-2 align-middle text-xs text-ink-2">
                    <span title={formatAbsolute(row.time)}>
                      {formatRelativeTime(row.time, { fallback: formatAbsolute(row.time) })}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-middle font-mono text-11 text-ink-2">
                    {row.type || '—'}
                    {row.position > 0 ? <span className="text-ink-4">[{row.position}]</span> : null}
                  </td>
                  <td
                    className="max-w-[16rem] truncate px-3 py-2 align-middle text-xs text-ink-2"
                    title={previewItem(row.result)}
                  >
                    {previewItem(row.result)}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    {isReviewableResultType(outputSchema, row.type) ? (
                      <StatusPill status={approvalStatusTone(row.approval_status)}>
                        {approvalLabel(row.approval_status)}
                      </StatusPill>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-middle text-right">
                    <Link
                      to={runPageHref('custom', row.run_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="font-mono text-11 text-brand hover:underline"
                    >
                      {row.run_id.slice(0, 8)}
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ItemDetailPanel({
  item,
  outputSchema,
  expectedRevision,
  readOnly
}: {
  item: CustomAgentResultItem
  outputSchema: Record<string, unknown>
  expectedRevision: number
  readOnly?: boolean
}) {
  const [feedback, setFeedback] = useState(item.feedback ?? '')
  const [patch, { isLoading }] = usePatchCustomAgentResultFeedbackMutation()
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [batchReview, { isLoading: batching }] = useBatchUpdateCustomAgentResultsMutation()
  const busy = isLoading || batching

  useEffect(() => {
    setFeedback(item.feedback ?? '')
    setReviewError(null)
  }, [item.id, item.feedback, expectedRevision])

  const itemSchema = useMemo(
    () => schemaForItemType(outputSchema, item.type),
    [outputSchema, item.type]
  )

  const submit = async (approval_status: CustomAgentResultApprovalStatus) => {
    const reason = feedback.trim()
    if (approval_status === 'rejected' && !reason) return
    setReviewError(null)
    const incidentReview = item.problem_occurrence_id
      ? {
          problem_occurrence_id: item.problem_occurrence_id,
          expected_revision: expectedRevision
        }
      : {}
    try {
      if (approval_status === 'rejected') {
        await batchReview({
          ...incidentReview,
          item_ids: [item.item_id || item.id],
          approval_status: 'rejected',
          feedback: reason
        }).unwrap()
        return
      }
      await patch({
        ...incidentReview,
        id: item.id,
        approval_status,
        feedback: reason || undefined
      }).unwrap()
    } catch (error) {
      setReviewError(
        error && typeof error === 'object' && 'status' in error && error.status === 409
          ? 'This finding changed. Refresh the results and review the current version.'
          : 'Failed to save feedback. Please try again.'
      )
    }
  }

  const reviewable = isReviewableResultType(outputSchema, item.type)

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-ink">
            {item.type || 'item'}
            {item.position > 0 ? (
              <span className="font-normal text-ink-3"> · #{item.position}</span>
            ) : null}
          </div>
          <div className="text-11 text-ink-3">
            {formatAbsolute(item.time)}
            <span className="text-ink-4"> · </span>
            <span className="font-mono">{item.run_id.slice(0, 8)}…</span>
          </div>
        </div>
        <Link
          to={runPageHref('custom', item.run_id)}
          className="text-xs font-medium text-brand transition-colors hover:underline"
        >
          Open run →
        </Link>
      </div>

      <SchemaAwareResultView value={item.result} schema={itemSchema} />

      {reviewable && !readOnly ? (
        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <label className="text-xs font-medium text-ink" htmlFor={`feedback-${item.id}`}>
            Feedback
          </label>
          <textarea
            id={`feedback-${item.id}`}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={2}
            placeholder="Required to reject."
            className="w-full resize-y rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink outline-none focus:border-brand"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy} onClick={() => void submit('approved')}>
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || feedback.trim().length === 0}
              onClick={() => void submit('rejected')}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void submit('awaiting_review')}
            >
              Reset to awaiting
            </Button>
            <StatusPill status={approvalStatusTone(item.approval_status)}>
              {approvalLabel(item.approval_status)}
            </StatusPill>
          </div>
          {reviewError ? (
            <p role="alert" className="m-0 text-xs text-down">
              {reviewError}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Prefer the property/items schema for a shred type; fall back to a free object. */
export function schemaForItemType(
  outputSchema: Record<string, unknown>,
  type: string
): Record<string, unknown> {
  const props = parseObjectSchema(outputSchema)
  const prop = props.find((p) => p.key === type)
  if (!prop) {
    return { type: 'object', additionalProperties: true }
  }
  if (prop.type === 'array') {
    const items = prop.schema.items
    if (items && typeof items === 'object' && !Array.isArray(items)) {
      return items as Record<string, unknown>
    }
    return {
      type: 'object',
      properties: { value: { type: 'string' } },
      additionalProperties: true
    }
  }
  if (prop.type === 'object') {
    return prop.schema
  }
  // Scalar top-level fields are shredded as { value: … }.
  return {
    type: 'object',
    properties: { value: prop.schema },
    required: ['value'],
    additionalProperties: false
  }
}

function previewItem(result: Record<string, unknown>): string {
  if (typeof result.value === 'string' || typeof result.value === 'number') {
    return formatScalar(result.value)
  }
  for (const key of ['title', 'headline', 'summary', 'equipment', 'stage', 'station']) {
    if (typeof result[key] === 'string' && result[key]) return String(result[key])
  }
  try {
    const s = JSON.stringify(result)
    return s.length > 80 ? `${s.slice(0, 77)}…` : s
  } catch {
    return '—'
  }
}

function approvalLabel(status: CustomAgentResultApprovalStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved'
    case 'rejected':
      return 'Rejected'
    default:
      return 'Awaiting review'
  }
}

function approvalStatusTone(status: CustomAgentResultApprovalStatus): Status {
  switch (status) {
    case 'approved':
      return 'run'
    case 'rejected':
      return 'down'
    default:
      return 'idle'
  }
}

function rowKey(row: CustomAgentResultItem): string {
  return row.id
}

function formatAbsolute(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}
